import React from 'react';

import { formatMontant } from '../../lib/formats';
import { TUILES_SITUATION } from '../../lib/lotSettlements';
import { cn } from '../../lib/utils';
import { StatutBadge } from '../ui/StatutBadge';
import { TableCell } from '../ui/table';

/**
 * Un montant en chiffre d'en-tête, en francs entiers: « 92 381 000 FCFA ». L'en-tête
 * abrège sinon les montants à partir du million (« 92,4 M FCFA »), ce qui montre
 * des décimales là où la règle est « FCFA sans décimales ».
 */
export const statMontant = ({ valeur, devise = 'XOF', ...reste }) => {
  const texte = formatMontant(valeur, devise);
  // `max-w-none`: la pastille s'élargit à la largeur du montant au lieu de le déborder.
  return { ...reste, valeur: texte, valeurCompacte: texte, className: 'max-w-none' };
};

/** Un montant de tableau : aligné à droite, chiffres tabulaires, rouge s'il est négatif. */
export const CelluleMontant = ({ valeur, devise = 'XOF', gras = false, discret = false, decimales, className }) => (
  <TableCell
    className={cn(
      'whitespace-nowrap text-right tabular-nums',
      Number(valeur) < 0 && 'text-destructive',
      gras && 'font-semibold',
      discret && 'text-muted-foreground',
      className,
    )}
  >
    {valeur === null || valeur === undefined ? '—' : formatMontant(valeur, devise, decimales)}
  </TableCell>
);

/** Un nombre entier de tableau (camions…), aligné à droite. */
export const CelluleEntier = ({ valeur, className }) => (
  <TableCell className={cn('whitespace-nowrap text-right tabular-nums', className)}>
    {Number(valeur) || 0}
  </TableCell>
);

/** Pastille de paiement (payé, payé en partie, à payer) ; un tiret quand il n'y a encore rien à payer. */
export const StatutPaiement = ({ code, ...props }) =>
  code ? <StatutBadge domaine="statut_paiement_etat" code={code} {...props} /> : <span className="text-muted-foreground">—</span>;

/** Pastille de statut d'un décompte (brouillon, validé, annulé). */
export const StatutDecompte = ({ code, ...props }) => <StatutBadge domaine="statut_decompte_lot" code={code} {...props} />;

const TONS_TUILE = {
  attention: 'text-flame-ink',
  positif: 'text-success',
  neutre: 'text-foreground',
};

/** Une tuile de montant : libellé discret, montant en chiffres tabulaires. */
export const TuileMontant = ({ libelleTuile, valeur, devise = 'XOF', ton = 'neutre', testid }) => (
  <div
    className="flex min-w-[8.5rem] flex-1 flex-col items-start gap-0.5 rounded-lg border border-border bg-card px-3 py-2"
    data-testid={testid}
  >
    <span className="text-xs text-muted-foreground">{libelleTuile}</span>
    <span className={cn('whitespace-nowrap text-base font-semibold tabular-nums', TONS_TUILE[ton] || TONS_TUILE.neutre)}>
      {formatMontant(valeur, devise)}
    </span>
  </div>
);

/**
 * Les sept chiffres d'une situation de paiement (`situation` du serveur) : valeur
 * confirmée, décompté, retenues, acomptes versés, payé, reste dû, net dû.
 */
export const TuilesSituation = ({ situation, testid = 'lots-paiements-tuiles' }) => {
  if (!situation) return null;
  const devise = situation.currency || 'XOF';
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} data-devise={devise}>
      {TUILES_SITUATION.map(([cle, texte]) => (
        <TuileMontant
          key={cle}
          testid={`${testid}-${cle}`}
          libelleTuile={texte}
          valeur={situation[cle]}
          devise={devise}
          ton={cle === 'remaining_due' && Number(situation[cle]) > 0 ? 'attention' : 'neutre'}
        />
      ))}
    </div>
  );
};
